import { cn } from "@/lib/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import type { ComponentPropsWithRef } from "react";
import React, { createContext, useContext } from "react";
import { type Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";

export type UnderlineTabsTheme = "tangerine" | "black";

interface UnderlineTabsContextValue {
  theme: UnderlineTabsTheme;
}

const UnderlineTabsContext = createContext<UnderlineTabsContextValue>({
  theme: "black",
});

interface UnderlineTabsProps extends ComponentPropsWithRef<typeof Tabs> {
  theme?: UnderlineTabsTheme;
}

export function UnderlineTabs({ theme = "tangerine", ...props }: UnderlineTabsProps) {
  return (
    <UnderlineTabsContext.Provider value={{ theme }}>
      <TabsPrimitive.Root className="relative mr-auto w-full" {...props} />
    </UnderlineTabsContext.Provider>
  );
}

export const UnderlineTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("w-full space-x-8 border-b border-borderLight text-textSubtle", className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

export const UnderlineTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { theme } = useContext(UnderlineTabsContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "relative whitespace-nowrap pb-4 text-sm font-semibold ring-offset-background focus-visible:outline-none disabled:pointer-events-none",
        "after:absolute after:bottom-[-1px] after:left-0 after:h-[2px] after:rounded-[30px] after:w-full after:opacity-0 after:transition-opacity",
        "data-[state=active]:after:opacity-100",
        "data-[state=active]:text-textPrimary",
        {
          "after:bg-brandTangerine": theme === "tangerine",
          "after:bg-textPrimary": theme === "black",
        },
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const UnderlineTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {}
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn("mt-6 focus-visible:outline-none", className)} {...props} />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
